/**
 * F5 Formulieren — pure functies voor template-/inzending-validatie.
 *
 * Het Zod-schema in @streep/types valideert de *statische* shape van een
 * inzending (`data` is `Record<string, unknown>`), maar kan niet weten of
 * de waarden inhoudelijk passen op de dynamische velden van een template.
 *
 * Deze module bevat de runtime-validatie: gegeven een lijst veld-definities
 * + een raw `data`-object, retourneer een lijst veldfouten en een
 * genormaliseerde `data`-record (alleen bekende keys, juiste types).
 *
 * 100% pure — géén DB-toegang, géén imports buiten @streep/types.
 */

import type {
  FormulierVeldDefinitie,
  FormulierVeldType,
} from '@streep/types'

export interface FormulierVeldFout {
  veldId: string
  message: string
}

export interface FormulierValidationResult {
  /** Alle veld-fouten; `valid` afgeleid uit `errors.length === 0`. */
  errors: FormulierVeldFout[]
  /** Genormaliseerde `data` — alleen bekende keys, met juiste types. */
  data: Record<string, FormulierVeldWaarde>
  /** True als geen errors. Convenience flag. */
  valid: boolean
}

/**
 * Toegestane runtime-waarden voor een ingevuld veld.
 * - `text` / `textarea` / `email` / `date` / `time` / `radio` / `select` → string
 * - `number` → number
 * - `checkbox` → boolean
 * - `multiselect` → string[]
 * - leeg/optioneel → null
 */
export type FormulierVeldWaarde =
  | string
  | number
  | boolean
  | string[]
  | null

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/
const ISO_TIME_RE = /^\d{2}:\d{2}$/

/**
 * Helper: check of een veld als "leeg" mag worden beschouwd.
 * Gebruikt om required-validatie te triggeren — niet om fout te markeren
 * voor optionele velden zonder waarde.
 */
function isEmpty(value: unknown): boolean {
  if (value === undefined || value === null) return true
  if (typeof value === 'string' && value.trim() === '') return true
  if (Array.isArray(value) && value.length === 0) return true
  return false
}

/**
 * Valideer + normaliseer één veld. Retourneert ofwel een fout-message of
 * een ge-coerced waarde van het juiste type.
 */
function validateVeld(
  veld: FormulierVeldDefinitie,
  raw: unknown,
): { error: string | null; value: FormulierVeldWaarde } {
  const required = veld.required === true

  // Leeg + niet required → null waarde, geen fout.
  if (isEmpty(raw)) {
    if (required) return { error: 'Dit veld is verplicht', value: null }
    return { error: null, value: null }
  }

  switch (veld.type satisfies FormulierVeldType) {
    case 'text':
    case 'textarea':
    case 'radio':
    case 'select': {
      if (typeof raw !== 'string') {
        return { error: 'Verwacht tekst-waarde', value: null }
      }
      const v = raw.trim()
      if (veld.minLength != null && v.length < veld.minLength) {
        return { error: `Minimaal ${veld.minLength} tekens`, value: v }
      }
      if (veld.maxLength != null && v.length > veld.maxLength) {
        return { error: `Maximaal ${veld.maxLength} tekens`, value: v }
      }
      if (veld.type === 'select' || veld.type === 'radio') {
        const allowed = (veld.options ?? []).map((o) => o.value)
        if (!allowed.includes(v)) {
          return { error: 'Ongeldige keuze', value: v }
        }
      }
      return { error: null, value: v }
    }

    case 'email': {
      if (typeof raw !== 'string') {
        return { error: 'Verwacht tekst-waarde', value: null }
      }
      const v = raw.trim()
      if (!EMAIL_RE.test(v)) {
        return { error: 'Ongeldig e-mailadres', value: v }
      }
      return { error: null, value: v }
    }

    case 'number': {
      const num =
        typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw) : NaN
      if (!Number.isFinite(num)) {
        return { error: 'Verwacht een getal', value: null }
      }
      if (veld.min != null && num < veld.min) {
        return { error: `Minimaal ${veld.min}`, value: num }
      }
      if (veld.max != null && num > veld.max) {
        return { error: `Maximaal ${veld.max}`, value: num }
      }
      return { error: null, value: num }
    }

    case 'date': {
      if (typeof raw !== 'string' || !ISO_DATE_RE.test(raw)) {
        return { error: 'Verwacht ISO-datum (yyyy-MM-dd)', value: null }
      }
      // Sanity-check: parse mag niet NaN opleveren.
      const t = Date.parse(`${raw}T00:00:00Z`)
      if (Number.isNaN(t)) {
        return { error: 'Ongeldige datum', value: raw }
      }
      return { error: null, value: raw }
    }

    case 'time': {
      if (typeof raw !== 'string' || !ISO_TIME_RE.test(raw)) {
        return { error: 'Verwacht tijd (HH:mm)', value: null }
      }
      const [h, m] = raw.split(':').map((s) => Number(s))
      if (h < 0 || h > 23 || m < 0 || m > 59) {
        return { error: 'Ongeldige tijd', value: raw }
      }
      return { error: null, value: raw }
    }

    case 'checkbox': {
      if (typeof raw === 'boolean') return { error: null, value: raw }
      if (raw === 'true') return { error: null, value: true }
      if (raw === 'false') return { error: null, value: false }
      return { error: 'Verwacht ja/nee', value: null }
    }

    case 'multiselect': {
      if (!Array.isArray(raw)) {
        return { error: 'Verwacht een lijst', value: null }
      }
      const allowed = new Set((veld.options ?? []).map((o) => o.value))
      const out: string[] = []
      for (const v of raw) {
        if (typeof v !== 'string') {
          return { error: 'Lijst-items moeten tekst zijn', value: null }
        }
        if (!allowed.has(v)) {
          return { error: `Ongeldige keuze: ${v}`, value: null }
        }
        out.push(v)
      }
      return { error: null, value: out }
    }
  }
}

export interface ValidateFormulierInzendingOpts {
  /**
   * Behoud keys uit `rawData` die niet in de template-velden voorkomen
   * (default: verwijderd). De waarden worden ongewijzigd (en ongevalideerd)
   * meegenomen.
   *
   * Bedoeld voor "side-channel"-keys die buiten het dynamische formulier-schema
   * om worden gevuld door andere endpoints — bv. de upload-/handtekening-routes
   * van de onboarding-self-flow (`_upload_*`, `handtekeningMeta`). Zonder deze
   * optie zou een normalisatie-pass die data wissen.
   *
   * Let op: de behouden keys overschrijven NOOIT een gevalideerde template-key
   * (template-velden winnen), zodat de validatie haar betekenis houdt.
   */
  preserveUnknownKeys?: boolean
}

/**
 * Valideer een volledige inzending tegen de template-velden.
 *
 * - Velden uit de template die niet in `data` zitten worden behandeld als leeg
 *   (kunnen alsnog falen op required).
 * - Keys in `data` die niet in de template staan worden standaard stil
 *   verwijderd — géén fout (template-versies kunnen evolueren; oude keys gewoon
 *   negeren). Met `opts.preserveUnknownKeys` blijven ze ongewijzigd staan
 *   (zie `ValidateFormulierInzendingOpts`).
 */
export function validateFormulierInzending(
  velden: FormulierVeldDefinitie[],
  rawData: Record<string, unknown>,
  opts?: ValidateFormulierInzendingOpts,
): FormulierValidationResult {
  const errors: FormulierVeldFout[] = []
  const out: Record<string, FormulierVeldWaarde> = {}

  const veldIds = new Set(velden.map((v) => v.id))

  // Eerst de niet-template (side-channel) keys neerzetten zodat een
  // gevalideerd template-veld ze daarna altijd overschrijft.
  if (opts?.preserveUnknownKeys) {
    for (const [key, value] of Object.entries(rawData)) {
      if (!veldIds.has(key)) {
        // `value` is ongevalideerde side-channel-data; cast naar het brede
        // waarde-type zonder normalisatie.
        out[key] = value as FormulierVeldWaarde
      }
    }
  }

  for (const veld of velden) {
    const raw = rawData[veld.id]
    const { error, value } = validateVeld(veld, raw)
    if (error) {
      errors.push({ veldId: veld.id, message: error })
    } else {
      out[veld.id] = value
    }
  }

  return { errors, data: out, valid: errors.length === 0 }
}

/**
 * Format een veld-waarde naar leesbare string voor exports/CSV.
 */
export function formatVeldWaarde(
  veld: FormulierVeldDefinitie,
  value: FormulierVeldWaarde,
): string {
  if (value === null || value === undefined) return ''
  switch (veld.type) {
    case 'checkbox':
      return value === true ? 'Ja' : 'Nee'
    case 'multiselect':
      if (!Array.isArray(value)) return ''
      return value
        .map((v) => veld.options?.find((o) => o.value === v)?.label ?? v)
        .join(', ')
    case 'select':
    case 'radio': {
      const found = veld.options?.find((o) => o.value === value)
      return found?.label ?? String(value)
    }
    case 'number':
      return String(value)
    default:
      return typeof value === 'string' ? value : String(value)
  }
}
